import { useMemo, useCallback, useEffect } from 'react';
import { useGetTableConfigurationByName, useSaveTableConfigurationMutation } from '@frontend/app/hooks';
import { TopAdData } from '../components/ads-table/ads-table-types';
import { AdsColumnDefinitionInput, defaultAdsColumns } from '../components/ads-table/ads-table-column-config';

// Table configuration constants
const ADS_TABLE_COLUMN_CONFIG_VERSION = 1;
const ADS_TABLE_NAME = 'social-ads-top-ads-table';

export interface UseSocialAdsTopAdsTabProps {
  isLoading: boolean;
  isRefreshing: boolean;
  data: TopAdData[];
  selectedColumns: AdsColumnDefinitionInput[];
  setSelectedColumns: (
    columns: AdsColumnDefinitionInput[] | ((prev: AdsColumnDefinitionInput[]) => AdsColumnDefinitionInput[]),
  ) => void;
  onSaveColumns?: (columns: string[]) => Promise<void>;
}

export function useSocialAdsTopAdsTab(params: {
  isLoading: boolean;
  isRefreshing: boolean;
  data: TopAdData[];
  selectedColumns: AdsColumnDefinitionInput[];
  setSelectedColumns: (
    columns: AdsColumnDefinitionInput[] | ((prev: AdsColumnDefinitionInput[]) => AdsColumnDefinitionInput[]),
  ) => void;
  onSaveColumns?: (columns: string[]) => Promise<void>;
}): UseSocialAdsTopAdsTabProps {
  // Destructure setSelectedColumns to avoid including entire params object in dependencies
  const { setSelectedColumns } = params;

  // Table configuration hooks
  const [saveTableConfiguration] = useSaveTableConfigurationMutation();
  const { data: tableConfigData, loading: tableConfigLoading } = useGetTableConfigurationByName({
    variables: { tableName: ADS_TABLE_NAME },
  });

  // Merge saved configuration with defaults
  const mergedColumns = useMemo(() => {
    if (tableConfigLoading) {
      return defaultAdsColumns;
    }

    if (
      tableConfigData?.getTableConfigurationByName?.columnDefinitions &&
      tableConfigData?.getTableConfigurationByName?.version === ADS_TABLE_COLUMN_CONFIG_VERSION
    ) {
      // Get saved configuration
      const savedColumns = tableConfigData.getTableConfigurationByName.columnDefinitions as AdsColumnDefinitionInput[];

      // Create a map of saved columns for quick lookup
      const savedColumnsMap = new Map(savedColumns.map((col) => [col.id, col]));

      // Merge saved configuration with defaults, preserving order
      const mergedColumns = defaultAdsColumns.map((defaultCol) => {
        const savedCol = savedColumnsMap.get(defaultCol.id);
        if (savedCol) {
          return {
            ...defaultCol,
            hidden: savedCol.hidden,
            order: savedCol.order,
          };
        }
        return defaultCol;
      });

      // Sort by the saved order
      return mergedColumns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    return defaultAdsColumns;
  }, [tableConfigLoading, tableConfigData]);

  // Apply merged columns when available
  useEffect(() => {
    if (mergedColumns && mergedColumns.length > 0 && !tableConfigLoading) {
      setSelectedColumns(mergedColumns);
    }
  }, [mergedColumns, tableConfigLoading, setSelectedColumns]);

  // Handle saving column preferences for Top Ads table
  const handleSaveAdsColumns = useCallback(
    async (columns: string[]) => {
      try {
        // Get current column order from the selected columns
        const currentColumns = params.selectedColumns;

        // Create updated column definitions preserving current order
        const updatedColumns = currentColumns.map((col, index) => ({
          ...col,
          hidden: !columns.includes(col.id),
          order: index + 1, // Preserve the current order
        }));

        // Save to backend
        await saveTableConfiguration({
          variables: {
            data: {
              version: ADS_TABLE_COLUMN_CONFIG_VERSION,
              tableName: ADS_TABLE_NAME,
              columnDefinitions: updatedColumns,
            },
          },
        });

        // Update local state
        setSelectedColumns(updatedColumns);
        console.log('Ads column preferences saved successfully');
      } catch (error) {
        console.error('Failed to save ads column preferences:', error);
        throw error;
      }
    },
    [params.selectedColumns, setSelectedColumns, saveTableConfiguration],
  );

  // Memoize the props to avoid unnecessary re-renders
  return useMemo(
    () => ({
      isLoading: params.isLoading,
      isRefreshing: params.isRefreshing,
      data: params.data,
      selectedColumns: params.selectedColumns,
      setSelectedColumns: params.setSelectedColumns,
      onSaveColumns: handleSaveAdsColumns,
    }),
    [
      params.isLoading,
      params.isRefreshing,
      params.data,
      params.selectedColumns,
      params.setSelectedColumns,
      handleSaveAdsColumns,
    ],
  );
}
